import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'convex/react'
import { ArrowLeft, BarChart3, Bot, Clock, MessageSquareQuote, RotateCcw, User as UserIcon } from 'lucide-react'
import { Button, Card } from '@/components/common'
import { useAuthStore } from '@/stores/authStore'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'

/**
 * Pilot-study analytics dashboard. Spec answer #33: time spent, AAI vs
 * self answers, branch depth distribution, revision count, average
 * questions per round. Driven entirely by the `logs:aggregateForUser`
 * Convex query.
 */
const AnalyticsPage = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const stats = useQuery(
    api.logs.aggregateForUser,
    user?.convexUserId
      ? { userId: user.convexUserId as unknown as Id<'users'> }
      : 'skip',
  )

  const histogramRows = useMemo(() => {
    if (!stats) return []
    const entries = Object.entries(stats.branchDepthHistogram)
      .map(([k, v]) => ({ depth: Number(k), count: v }))
      .sort((a, b) => a.depth - b.depth)
    const max = entries.reduce((m, r) => Math.max(m, r.count), 0) || 1
    return entries.map((r) => ({ ...r, pct: (r.count / max) * 100 }))
  }, [stats])

  const minutes = stats ? Math.round(stats.totalDurationMs / 60000) : 0

  return (
    <div className="container mx-auto px-6 py-8 max-w-5xl">
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ArrowLeft className="w-4 h-4" />}
          onClick={() => navigate('/projects')}
        >
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Pilot Study Analytics
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Aggregate metrics across every project on this account.
          </p>
        </div>
      </div>

      {!stats ? (
        <Card className="p-8 text-center text-slate-500 dark:text-slate-400">
          Loading analytics…
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Top-line cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={<Clock className="w-5 h-5" />}
              label="Time spent"
              value={`${minutes} min`}
              subtext={`${stats.projectCount} project(s)`}
            />
            <StatCard
              icon={<Bot className="w-5 h-5" />}
              label="AAI invocations"
              value={String(stats.aaiInvocations)}
              subtext={`${stats.aaiAssistedAnswers} answers used AAI`}
            />
            <StatCard
              icon={<UserIcon className="w-5 h-5" />}
              label="Self answers"
              value={String(stats.selfAnswers)}
              subtext={`${stats.humanAnswers} total`}
            />
            <StatCard
              icon={<RotateCcw className="w-5 h-5" />}
              label="Revisions"
              value={String(stats.totalRevisions)}
              subtext="OAI revision requests"
            />
          </div>

          {/* Avg questions per round */}
          <Card className="p-6">
            <h2 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <MessageSquareQuote className="w-5 h-5" />
              Question density
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Average questions per round:{' '}
              <span className="font-semibold text-slate-900 dark:text-white">
                {stats.avgQuestionsPerRound.toFixed(2)}
              </span>{' '}
              · Total questions:{' '}
              <span className="font-semibold text-slate-900 dark:text-white">
                {stats.totalQuestions}
              </span>
            </p>
          </Card>

          {/* Branch depth histogram */}
          <Card className="p-6">
            <h2 className="font-semibold text-slate-900 dark:text-white mb-3">
              Branch depth distribution
            </h2>
            {histogramRows.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No branches yet.
              </p>
            ) : (
              <div className="space-y-2">
                {histogramRows.map((row) => (
                  <div key={row.depth} className="flex items-center gap-3">
                    <span className="w-16 text-xs text-slate-500">
                      Depth {row.depth}
                    </span>
                    <div className="flex-1 h-4 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                      <div
                        className="h-full bg-light-icy-blue dark:bg-dark-muted-teal"
                        style={{ width: `${row.pct}%` }}
                      />
                    </div>
                    <span className="w-12 text-right text-xs font-medium text-slate-600 dark:text-slate-300">
                      {row.count}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  )
}

const StatCard = ({
  icon,
  label,
  value,
  subtext,
}: {
  icon: React.ReactNode
  label: string
  value: string
  subtext?: string
}) => (
  <Card className="p-4">
    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wide">
      {icon}
      <span>{label}</span>
    </div>
    <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
      {value}
    </div>
    {subtext && (
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
        {subtext}
      </div>
    )}
  </Card>
)

export default AnalyticsPage
